import { useState } from "react";
import { CheckIcon as CheckIconOutline } from "@heroicons/react/24/outline";
import { CheckIcon as CheckIconSolid } from "@heroicons/react/24/solid";
import { Button, Label, TextArea, TextField, Tooltip } from "@heroui/react";
import { useLogRecipe } from "@/hooks/useLogRecipe";
import { useUnlogToday } from "@/hooks/useUnlogToday";
import { useUpdateRecipeLogNote } from "@/hooks/useUpdateRecipeLogNote";
import type { RecipeLog } from "@/features/RecipeLog/types";

interface MarkAsMadePanelProps {
    readonly recipeId: number;
}

export const MarkAsMadePanel = ({ recipeId }: MarkAsMadePanelProps) => {
    const logRecipe = useLogRecipe();
    const unlogToday = useUnlogToday();
    const updateNote = useUpdateRecipeLogNote();

    // The panel always opens in the prompt state, every visit — it's a "log it
    // now" action, not a reflection of past history (the cook log below shows
    // history). `todaysLog` holds the entry created this session so the user can
    // attach a note and undo it.
    const [todaysLog, setTodaysLog] = useState<RecipeLog | null>(null);
    const [noteDraft, setNoteDraft] = useState("");

    const handleMarkAsMade = () => {
        logRecipe.mutate(
            { recipeId },
            {
                onSuccess: (log) => {
                    setTodaysLog(log);
                    setNoteDraft(log.note);
                }
            }
        );
    };

    const handleUndo = () => {
        unlogToday.mutate(
            { recipeId },
            {
                onSuccess: () => {
                    setTodaysLog(null);
                    setNoteDraft("");
                }
            }
        );
    };

    const handleSaveNote = () => {
        if (!todaysLog) return;
        updateNote.mutate(
            { logId: todaysLog.id, recipeId, note: noteDraft },
            {
                onSuccess: () => setTodaysLog({ ...todaysLog, note: noteDraft.trim() })
            }
        );
    };

    if (todaysLog) {
        const noteUnchanged = noteDraft.trim() === todaysLog.note.trim();
        return (
            <section
                aria-label="Made it status"
                className="mx-auto w-full max-w-lg rounded-2xl border border-mystic-200 bg-mystic-50/60 px-5 py-5 sm:px-6"
            >
                <div className="flex items-center justify-center gap-2.5 text-mystic-700">
                    <CheckIconSolid className="size-5" />
                    <p className="font-serif text-lg">You made this recipe! Cook log updated.</p>
                </div>

                <div className="mt-4">
                    <TextField>
                        <Label className="font-display text-[11px] uppercase tracking-[0.14em] text-mystic-600">
                            Add a note <span className="normal-case tracking-normal text-mystic-500">(optional)</span>
                        </Label>
                        <TextArea
                            variant="secondary"
                            className="mt-1.5 w-full"
                            rows={2}
                            value={noteDraft}
                            onChange={(event) => setNoteDraft(event.target.value)}
                            placeholder="e.g., Doubled the garlic, baked 5 min longer."
                        />
                    </TextField>

                    <div className="mt-3 flex items-center justify-between gap-3">
                        <Tooltip delay={0}>
                            <Tooltip.Trigger>
                                <button
                                    type="button"
                                    onClick={handleUndo}
                                    disabled={unlogToday.isPending}
                                    className="font-display text-[11px] uppercase tracking-[0.14em] text-mystic-600 hover:text-mystic-800 underline-offset-4 hover:underline cursor-pointer disabled:opacity-50"
                                >
                                    {unlogToday.isPending ? "Removing..." : "Undo"}
                                </button>
                            </Tooltip.Trigger>
                            <Tooltip.Content>Remove today's log entry</Tooltip.Content>
                        </Tooltip>

                        <Button
                            variant="secondary"
                            size="sm"
                            onPress={handleSaveNote}
                            isDisabled={noteUnchanged}
                            isPending={updateNote.isPending}
                        >
                            {({ isPending }: { isPending: boolean }) => (isPending ? "Saving..." : "Save note")}
                        </Button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            aria-label="Mark as made"
            className="mx-auto w-full max-w-lg rounded-2xl border border-dashed border-primary-300/70 bg-primary-50/60 px-5 py-4 sm:px-6 sm:py-5"
        >
            <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:justify-between sm:gap-4 sm:text-left">
                <p className="font-display text-[12px] uppercase tracking-[0.18em] text-primary-700">
                    Did you make this recipe?
                </p>
                <Button onPress={handleMarkAsMade} isPending={logRecipe.isPending} size="sm">
                    {({ isPending }: { isPending: boolean }) => (
                        <>
                            <CheckIconOutline className="size-4" strokeWidth={2} />
                            {isPending ? "Logging..." : "Mark as made"}
                        </>
                    )}
                </Button>
            </div>
        </section>
    );
};
